import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { AbsenceRequest, AbsenceRequestStatus, AbsenceType } from '../../services/absence.service'
import { useLocale } from '../../hooks/use-locale'

interface AbsenceCalendarProps {
  requests: AbsenceRequest[]
}

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const

const TYPE_COLORS: Record<AbsenceType, { bg: string; text: string; dot: string }> = {
  [AbsenceType.VACATION]: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    dot: 'bg-blue-500',
  },
  [AbsenceType.SICK_LEAVE]: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    dot: 'bg-red-500',
  },
  [AbsenceType.UNPAID_LEAVE]: {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    dot: 'bg-amber-500',
  },
  [AbsenceType.OTHER]: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    dot: 'bg-gray-500',
  },
}

interface DayAbsence {
  name: string
  type: AbsenceType
  userId: string
  requestId: string
}

export default function AbsenceCalendar({ requests }: AbsenceCalendarProps) {
  const { t } = useTranslation()
  const { formatDate } = useLocale()
  const today = new Date()
  const [currentMonth, setCurrentMonth] = useState(today.getMonth())
  const [currentYear, setCurrentYear] = useState(today.getFullYear())
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  const getTypeLabel = (type: AbsenceType) => {
    switch (type) {
      case AbsenceType.VACATION:
        return t('absence.types.vacation')
      case AbsenceType.SICK_LEAVE:
        return t('absence.types.sickLeave')
      case AbsenceType.UNPAID_LEAVE:
        return t('absence.types.unpaidLeave')
      case AbsenceType.OTHER:
        return t('absence.types.other')
    }
  }

  const currentMonthName = t(`absence.calendar.months.${MONTH_KEYS[currentMonth]}`)

  const absenceMap = useMemo(() => {
    const map: Record<string, DayAbsence[]> = {}

    requests
      .filter(r => r.status === AbsenceRequestStatus.APPROVED)
      .forEach(request => {
        const start = new Date(request.startDate + 'T00:00:00')
        const end = new Date(request.endDate + 'T00:00:00')

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
          if (!map[dateKey]) {
            map[dateKey] = []
          }
          map[dateKey].push({
            name: `${request.userFirstName || ''} ${request.userLastName || ''}`.trim(),
            type: request.type,
            userId: request.userId,
            requestId: request.id,
          })
        }
      })

    return map
  }, [requests])

  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1)
    const lastDay = new Date(currentYear, currentMonth + 1, 0)

    let startDow = firstDay.getDay() - 1
    if (startDow < 0) startDow = 6

    const days: Array<{ date: Date; dateKey: string; isCurrentMonth: boolean }> = []

    for (let i = startDow - 1; i >= 0; i--) {
      const d = new Date(currentYear, currentMonth, -i)
      days.push({
        date: d,
        dateKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
        isCurrentMonth: false,
      })
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      const d = new Date(currentYear, currentMonth, i)
      days.push({
        date: d,
        dateKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
        isCurrentMonth: true,
      })
    }

    const remaining = 7 - (days.length % 7)
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const d = new Date(currentYear, currentMonth + 1, i)
        days.push({
          date: d,
          dateKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
          isCurrentMonth: false,
        })
      }
    }

    return days
  }, [currentMonth, currentYear])

  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11)
      setCurrentYear(y => y - 1)
    } else {
      setCurrentMonth(m => m - 1)
    }
    setSelectedDay(null)
  }

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0)
      setCurrentYear(y => y + 1)
    } else {
      setCurrentMonth(m => m + 1)
    }
    setSelectedDay(null)
  }

  const goToToday = () => {
    setCurrentMonth(today.getMonth())
    setCurrentYear(today.getFullYear())
    setSelectedDay(null)
  }

  const monthAbsentees = useMemo(() => {
    const uniqueUsers = new Map<string, { name: string; days: number; types: Set<AbsenceType> }>()

    calendarDays
      .filter(d => d.isCurrentMonth)
      .forEach(day => {
        const absences = absenceMap[day.dateKey] || []
        absences.forEach(a => {
          const existing = uniqueUsers.get(a.userId)
          if (existing) {
            existing.days++
            existing.types.add(a.type)
          } else {
            uniqueUsers.set(a.userId, { name: a.name, days: 1, types: new Set([a.type]) })
          }
        })
      })

    return Array.from(uniqueUsers.values()).sort((a, b) => b.days - a.days)
  }, [calendarDays, absenceMap])

  const selectedDayAbsences = selectedDay ? absenceMap[selectedDay] || [] : []

  const formatFullDate = (dateKey: string) =>
    formatDate(dateKey + 'T00:00:00', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })

  return (
    <div className='space-y-6'>
      <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
        <div className='flex items-center justify-between px-6 py-4 border-b border-gray-100'>
          <div className='flex items-center gap-3'>
            <button
              onClick={goToPreviousMonth}
              className='p-1.5 rounded-lg hover:bg-gray-100 transition-colors'
            >
              <ChevronLeft className='w-5 h-5 text-gray-600' />
            </button>
            <h2 className='text-lg font-semibold text-gray-900 min-w-[200px] text-center'>
              {currentMonthName} {currentYear}
            </h2>
            <button
              onClick={goToNextMonth}
              className='p-1.5 rounded-lg hover:bg-gray-100 transition-colors'
            >
              <ChevronRight className='w-5 h-5 text-gray-600' />
            </button>
          </div>
          <button
            onClick={goToToday}
            className='text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors'
          >
            {t('absence.calendar.today')}
          </button>
        </div>

        <div className='grid grid-cols-7 border-b border-gray-100'>
          {WEEKDAY_KEYS.map(day => (
            <div
              key={day}
              className='py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider'
            >
              {t(`absence.calendar.weekdays.${day}`)}
            </div>
          ))}
        </div>

        <div className='grid grid-cols-7'>
          {calendarDays.map((day, idx) => {
            const absences = absenceMap[day.dateKey] || []
            const isToday = day.dateKey === todayKey
            const isSelected = day.dateKey === selectedDay
            const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6

            return (
              <button
                key={idx}
                onClick={() => setSelectedDay(day.dateKey === selectedDay ? null : day.dateKey)}
                className={`
                  relative min-h-[80px] p-1.5 border-b border-r border-gray-100 text-left transition-colors
                  ${!day.isCurrentMonth ? 'bg-gray-50/50' : isWeekend ? 'bg-gray-50/30' : 'bg-white'}
                  ${isSelected ? 'ring-2 ring-blue-500 ring-inset z-10' : ''}
                  hover:bg-blue-50/50
                `}
              >
                <span
                  className={`
                    inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full
                    ${isToday ? 'bg-blue-600 text-white' : ''}
                    ${!day.isCurrentMonth ? 'text-gray-400' : 'text-gray-700'}
                  `}
                >
                  {day.date.getDate()}
                </span>

                <div className='mt-0.5 space-y-0.5'>
                  {absences.slice(0, 3).map((absence, aIdx) => (
                    <div
                      key={`${absence.userId}-${aIdx}`}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded truncate ${TYPE_COLORS[absence.type].bg} ${TYPE_COLORS[absence.type].text}`}
                    >
                      {absence.name.split(' ')[0]}
                    </div>
                  ))}
                  {absences.length > 3 && (
                    <div className='text-[10px] leading-tight text-gray-500 px-1'>
                      {t('absence.calendar.moreOverflow', {
                        defaultValue: '+{{count}} mehr',
                        count: absences.length - 3,
                      })}
                    </div>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {selectedDay && (
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>{formatFullDate(selectedDay)}</h3>
          {selectedDayAbsences.length === 0 ? (
            <p className='text-gray-500 text-sm'>{t('absence.calendar.noAbsencesOnDay')}</p>
          ) : (
            <div className='space-y-2'>
              {selectedDayAbsences.map((absence, idx) => (
                <div
                  key={`${absence.userId}-${idx}`}
                  className='flex items-center gap-3 p-3 rounded-lg bg-gray-50'
                >
                  <div
                    className={`w-2.5 h-2.5 rounded-full shrink-0 ${TYPE_COLORS[absence.type].dot}`}
                  />
                  <div className='flex-1 min-w-0'>
                    <p className='font-medium text-gray-900 text-sm'>{absence.name}</p>
                    <p className='text-xs text-gray-500'>{getTypeLabel(absence.type)}</p>
                  </div>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${TYPE_COLORS[absence.type].bg} ${TYPE_COLORS[absence.type].text}`}
                  >
                    {getTypeLabel(absence.type)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {monthAbsentees.length > 0 && (
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>
            {t('absence.calendar.monthSummary', { month: currentMonthName })}
          </h3>
          <div className='space-y-2'>
            {monthAbsentees.map((person, idx) => (
              <div
                key={idx}
                className='flex items-center justify-between p-3 rounded-lg bg-gray-50'
              >
                <div className='flex items-center gap-3'>
                  <div className='w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600'>
                    {person.name
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                  <div>
                    <p className='font-medium text-sm text-gray-900'>{person.name}</p>
                    <div className='flex gap-1 mt-0.5'>
                      {Array.from(person.types).map(type => (
                        <span
                          key={type}
                          className={`text-[10px] px-1.5 py-0.5 rounded-full ${TYPE_COLORS[type].bg} ${TYPE_COLORS[type].text}`}
                        >
                          {getTypeLabel(type)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <span className='text-sm font-semibold text-gray-700'>
                  {t('common.days', { count: person.days })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className='flex flex-wrap gap-4 px-1'>
        {Object.values(AbsenceType).map(type => (
          <div key={type} className='flex items-center gap-2'>
            <div className={`w-3 h-3 rounded-full ${TYPE_COLORS[type].dot}`} />
            <span className='text-xs text-gray-600'>{getTypeLabel(type)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
